import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
    useTargetPerson,
    useAccessRequestPolicy,
    useCart,
    useAddItemToCart,
    useRenewableAssignmentInfo,
} from 'hooks'
import {
    Tooltip,
    Attribute,
    AttributeLabel,
    AttributeValue,
    IconButton,
    Loader,
} from 'packages/eid-ui'
import { Icon } from 'packages/eid-icons'
import { Box, Typography, useTheme, Dialog } from '@mui/material'
import { ItemDetails } from 'components'
import { useTimeConstrainedControl } from 'containers/ListingPage/Computers/TimeConstrainedControl'
import { CART_REQUEST_TYPES } from 'utils'
import { useNotification } from 'packages/eid-ui'

const ResourceExtendPopUp = ({
    item,
    handleClose,
    extendSessionMutation,
    getExtendPayload,
}) => {
    const resource = item.resource ? item.resource : item
    const assignment = item?.assignment

    // const [disableButton, setDisableButton] = useState(false)
    const isBusinessRole = item?.resource?.resourceTypeName === 'BusinessRole'
    const isAzureRoles = item?.resource?.resourceTypeName === 'AzureRole'
    const [enableApiCall, setEnableApiCall] = useState(false)
    const { t } = useTranslation()
    const theme = useTheme()
    const { data: cart } = useCart()
    const [targetPerson] = useTargetPerson()
    const requestPolicy = useAccessRequestPolicy(resource.requestPolicyId)
    const { mutateAsync: addItemToCart, isLoading } = useAddItemToCart()
    const { mutateAsync: extendSession, isLoading: isExtending } =
        extendSessionMutation
    const { showWarningMessage, showSuccessMessage } = useNotification()

    const { isInitialLoading, data: renewableData } =
        useRenewableAssignmentInfo(
            item.assignment?.personId || targetPerson?.id,
            isBusinessRole
                ? item.assignment?.resourceAssignment?.resourceAssignmentId
                : item.assignment?.resourceAssignment?.resourceId ||
                      resource?.id,
            isBusinessRole,
            enableApiCall,
            true,
        )

    const {
        selectedStartDate,
        selectedEndDate,
        hasInvalidDates,
        timeConstrainedControl,
    } = useTimeConstrainedControl(requestPolicy, true, false, false, true)

    const [open, setOpen] = useState(true)
    const [duration, setDuration] = useState(0)

    useEffect(() => {
        if (requestPolicy) {
            setDuration(requestPolicy.defaultValueInMinutes)
        }
    }, [requestPolicy])

    useEffect(() => {
        if (enableApiCall && renewableData && !renewableData.isRenewable) {
            if (!isItemAlreadyInCart()) {
                handleAddToCart()
            } else {
                showWarningMessage(t('Common_ItemAlreadyInCart'))
            }
        } else if (
            enableApiCall &&
            renewableData &&
            renewableData.isRenewable
        ) {
            const payload = getExtendPayload(
                resource,
                selectedEndDate,
                item.assignment?.resourceAssignment?.resourceAssignmentId,
            )
            extendSession(payload)
                .then(() => {
                    onClose()
                    showSuccessMessage(t('Common_ExtendSuccess'))
                })
                .catch(() => showWarningMessage(t('Common_ExtendError')))
        }
        return () => {}
    }, [isInitialLoading, renewableData, enableApiCall])

    const handleExtend = () => {
        if (assignment?.isPreApproved || resource.isPreApproved) {
            setEnableApiCall(true)
        } else {
            handleAddToCart()
        }
    }

    const isItemAlreadyInCart = () => {
        let targetPersonCart = null
        if (cart && cart.length > 0) {
            targetPersonCart = cart.filter(
                (el) => el.targetPersonId === item.assignment?.personId,
            )
        } else {
            targetPersonCart = cart
        }
        if (targetPersonCart) {
            const currentItem = targetPersonCart.cartItems.filter(
                (el) =>
                    el.requestableResourceId === item.resource?.id &&
                    el.locationId ===
                        item.assignment.resourceAssignment.locationId,
            )

            if (currentItem && currentItem.length > 0) {
                return true
            }
        }

        return false
    }

    const handleAddToCart = () => {
        if (!isItemAlreadyInCart()) {
            const itemToAdd = {
                assignmentId:
                    (isAzureRoles ? resource?.assignmentId : resource?.id) ||
                    resource?.id,
                assignmentType: CART_REQUEST_TYPES.UpdateTimeConstrain,
                requestableResourceId:
                    assignment.resourceAssignment?.resourceId,
                resource_FriendlyName: resource.friendlyName,
                resourceTypeId: resource.resourceTypeId,
                resourceType_Name: resource.resourceTypeName,
                resourceTypeRoleId:
                    resource?.checkedOutCredentialId ||
                    resource?.applicationId ||
                    assignment?.resourceAssignment?.resourceAssignmentId,
                targetPersonId: targetPerson?.id,
                timeConstraintActive: true,
                startDateUtc: selectedStartDate,
                endDateUtc: selectedEndDate,
                ...(assignment?.resourceAssignment?.locationId && {
                    locationId: assignment.resourceAssignment.locationId,
                }),
            }
            addItemToCart(itemToAdd)
                .then(() => {
                    onClose()
                })
                .catch((err) => console.error(err))
        } else {
            showWarningMessage(t('Common_ItemAlreadyInCart'))
        }
    }

    const onClose = () => {
        setOpen(false)
        handleClose()
    }

    const durationSelectorSection = (
        <Box style={{ position: 'relative' }}>
            {requestPolicy?.isTimeConstrained && (
                <Attribute orientation="horizontal">
                    <AttributeLabel
                        sx={{
                            minWidth: '220px',
                        }}
                    >
                        {t('Common_DefaultAccessDuration')}
                    </AttributeLabel>
                    <AttributeValue>
                        {requestPolicy && requestPolicy.defaultValueInMinutes}
                    </AttributeValue>
                </Attribute>
            )}

            {requestPolicy?.isTimeConstrained && (
                <Attribute orientation="horizontal">
                    <AttributeLabel
                        sx={{
                            minWidth: '220px',
                        }}
                    >
                        {t('Common_MaxAccessDuration')}
                    </AttributeLabel>
                    <AttributeValue>
                        {requestPolicy && requestPolicy.maximumValueInMinutes}
                    </AttributeValue>
                </Attribute>
            )}
            <Box marginLeft={'-2.4rem'}>
                {timeConstrainedControl ? timeConstrainedControl : <Loader />}
            </Box>
        </Box>
    )

    const getTooltipTitle = () => {
        const itemAlreadyInCart =
            !resource.isPreApproved &&
            cart?.cartItems.find(
                (c) => c.requestableResourceId === resource.id,
            ) !== undefined

        let message = ''
        if (duration < 1 || hasInvalidDates) {
            message = t('Common_InvalidDuration')
        }

        if (message === '' && itemAlreadyInCart) {
            message = t('Common_ItemAlreadyInCart')
        }
        return message
    }

    return (
        <Dialog
            sx={{
                '& .MuiPaper-root': {
                    width: '100%',
                },
            }}
            scroll={'body'}
            open={open}
            onClose={() => {
                onClose()
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    height: '80px',
                    alignItems: 'center',
                    padding: '16px 12px 16px 32px',
                    backgroundColor: theme?.palette?.background?.paper,
                }}
            >
                <Typography
                    sx={{
                        fontSize: '18px',
                        fontWeight: 'bold',
                        color: '#3b454d',
                    }}
                >
                    {t('ExtendSession')}
                </Typography>

                <Box display="flex">
                    <IconButton
                        onClick={() => {
                            onClose()
                        }}
                        size="large"
                    >
                        <Icon name="Close" color="#959598" />
                    </IconButton>
                </Box>
            </Box>
            <Box
                sx={{
                    overflowWrap: 'break-word',
                    wordBreak: 'normal',
                    textTransform: 'uppercase',
                    color: `${theme?.palette?.common?.black} !important`,
                    overflow: 'hidden',
                    lineHeight: '15px',
                    fontSize: '14px',
                    padding: '15px 31px',
                    borderTop: '1px solid rgba(0, 0, 0, .05)',
                    borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
                    fontWeight: 'bold',
                    boxShadow: '0 2px 16px 0 rgba(0, 0, 0, 0.11)',
                    backgroundImage:
                        'linear-gradient(0deg, rgb(0, 0, 0, 0.02) 12.5%, rgb(255, 255, 255) 12.5%, rgb(255, 255, 255) 50%, rgb(0, 0, 0, 0.02) 50%, rgb(0, 0, 0, 0.02) 62.5%, rgb(255, 255, 255) 62.5%, rgb(255, 255, 255) 100%)',
                    backgroundSize: '8.00px 8.00px',
                    '& svg': {
                        margin: '0px !important',
                    },
                }}
            >
                {t('SetDuration')}
            </Box>
            <Box
                sx={{
                    backgroundColor: '#fbfbfd',
                    padding: '24px 40px',
                    boxShadow: '0 2px 16px 0 rgba(0, 0, 0, 0.11)',
                    display: 'flex',
                    '&:focus': {
                        outline: 'none',
                    },
                }}
            >
                <Box marginTop={'2rem'}>{durationSelectorSection}</Box>
            </Box>
            <Box
                sx={{
                    display: 'flex',
                    padding: '16px 12px 16px 32px',
                    paddingTop: '16px',
                }}
            >
                <Tooltip title={getTooltipTitle()}>
                    <Box minWidth="167px">
                        <ItemDetails.ActionButton
                            fontColor={theme?.palette?.common?.white}
                            bgColor={theme?.palette?.primary?.main}
                            width="fit-content"
                            minWidth="100%"
                            loading={
                                isLoading || isExtending || isInitialLoading
                            }
                            disabled={getTooltipTitle() !== ''}
                            onClick={handleExtend}
                        >
                            <Typography
                                style={{
                                    color: theme?.palette?.common?.white,
                                }}
                            >
                                {t('Submit')}
                            </Typography>
                        </ItemDetails.ActionButton>
                    </Box>
                </Tooltip>
            </Box>
        </Dialog>
    )
}

export default ResourceExtendPopUp
